import { Card, Center, Heading, Progress, Spinner, Text, ToastId, useToast } from "@chakra-ui/react"
import { RenderJob } from "./addMediaButton"
import { useEffect, useRef, useState } from "react"
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'
import VideoTrimmer from "./videoTrimmer"
import { MediaMeta } from "@/pages/mediaManager"

const VideoRenderer: React.FC<{
	video: File | undefined,
	onValidate: (newMedia: MediaMeta) => string | undefined,
	onUpload: (newMedia: MediaMeta, file: File, icon?: File) => void
	onSubmit: () => void // Will pop a value off the edit stack
	onCloseDialouge: () => void
}> = ({ video, onValidate, onUpload, onSubmit, onCloseDialouge }) => {
	const toast = useToast()

	const ffmpegRef = useRef(new FFmpeg())

	const [renderQueue, setRenderQueue] = useState<RenderJob[]>([])
	const [ffmpegLoaded, setFfmpegLoaded] = useState(false)
	const [progress, setProgress] = useState(0)
	const rendering = useRef(false)
	const toastRef = useRef<ToastId | undefined>(undefined)

	useEffect(() => {
		async function loadFFmpegCore() {

			const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm'
			const ffmpeg = ffmpegRef.current
			ffmpeg.on('progress', ({ progress }) => {
				setProgress(progress)
			})
			await ffmpeg.load({
				coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
				wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
			})
			setFfmpegLoaded(true)
		}
		if (!ffmpegLoaded) loadFFmpegCore()
	})

	useEffect(() => {
		if (toastRef.current) toast.update(toastRef.current as ToastId, {
			render: () => (
				<Card>
					<Center borderRadius={5} gap={3} padding={3} flexDir='column'>
						<Text w='full' textAlign='left'>Rendering <Text as='b'>{renderQueue.length}</Text> videos in background</Text>
						<Progress borderRadius='full' value={progress} max={1} hasStripe w='100%' />
					</Center>
				</Card>
			)
		})
		else if (!toastRef.current && renderQueue.length !== 0) {
			toastRef.current = toast({
				duration: null,
				position: 'bottom-left',
			})
		}
		else {
			toast.close(toastRef.current as ToastId)
			toastRef.current = undefined
		}
	}, [renderQueue, progress, toast])


	useEffect(() => {
		async function cropAndTrimVideo() {
			rendering.current = true
			const ffmpeg = ffmpegRef.current
			const job = renderQueue[0]!
			await ffmpeg.writeFile('inputFile', await fetchFile(job.newMedia.fileUrl))
			await ffmpeg.exec([
				'-i',
				'inputFile',
				'-vf',
				job.mode === 'contain' ?
					`trim=${job.trim.start}:${job.trim.end}, scale=600:400:force_original_aspect_ratio=decrease, pad=600:400:(ow-iw)/2:(oh-ih)/2, setpts=PTS-STARTPTS` :
					`trim=${job.trim.start}:${job.trim.end}, crop=${(job.cropBox.width).toFixed(0)}:${(job.cropBox.height).toFixed(0)}:${(job.cropBox.x).toFixed(0)}:${(job.cropBox.y).toFixed(0)}, scale=600:400, setpts=PTS-STARTPTS`,
				'-af',
				`atrim=${job.trim.start}:${job.trim.end}, asetpts=PTS-STARTPTS`,
				`outputFile.mp4`,
			])
			const data = await ffmpeg.readFile('outputFile.mp4')
			const blob = new Blob([data], { type: 'video/mp4' })
			const finalVideo = new File([blob], `${job.newMedia.name}.mp4`, { type: 'video/mp4' })
			await ffmpeg.exec([
				'-i',
				'outputFile.mp4', // Input
				'-ss',
				'00:00:00', // Take a screenshot starting at time 0
				'-frames:v',
				'1', // Take only one frame
				'-q:v',
				'10', // Set screenshot compression (1-32) higher is more compressed
				'thumbnail.jpg', // Set output file
			])
			const thumbnailData = await ffmpeg.readFile('thumbnail.jpg')
			const thumbnailBlob = new Blob([thumbnailData], { type: 'image/jpeg' })
			const thumbnail = new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' })
			// Set an upload media to our final media output
			onUpload({ ...job.newMedia, fileUrl: URL.createObjectURL(finalVideo), iconUrl: URL.createObjectURL(thumbnail) }, finalVideo, thumbnail)
			rendering.current = false
			setRenderQueue(currentQueue => currentQueue.slice(1))
		}
		if (!rendering.current && renderQueue.length && ffmpegLoaded) cropAndTrimVideo()
	}, [renderQueue])

	useEffect(() => {
		if (!video && renderQueue.length === 0) {
			if (toastRef.current) toast.close(toastRef.current)
			onCloseDialouge()
		}
	}, [video, renderQueue])

	if (!video) {
		return (
			<Center flexDir='column' height='60vh' gap={5}>
				<Spinner boxSize={100} />
				<Heading textAlign='center'>
					Rendering videos in background
					<br />
					Please don't close
				</Heading>
			</Center>
		)
	}

	if (!video.type.includes('video')) {
		return <></>
	}
	return (
		<VideoTrimmer
			video={video}
			onValidate={(newMedia) => {
				if (renderQueue.find((job) => job.newMedia.name === newMedia.name)) return "Name must be unique"
				return onValidate(newMedia)
			}}
			onSubmit={(job) => {
				setRenderQueue([...renderQueue, job])
				onSubmit()
			}}

		/>
	)
}

export default VideoRenderer
